import { useState } from "react";
import axios from "axios";
import { BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-0 border-b border-[rgba(212,175,55,0.3)] bg-transparent py-3 font-lora text-sm text-abbey-cream placeholder:text-abbey-cream/35 focus:border-abbey-gold focus:outline-none transition-colors duration-300";

const errorText = (detail) =>
  typeof detail === "string"
    ? detail
    : Array.isArray(detail)
      ? detail.map((e) => e?.msg || "").filter(Boolean).join(" ")
      : "Could not subscribe right now. Please try again.";

export default function NewsletterSignup({ idPrefix = "newsletter", compact = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { name, email });
      if (data.status === "already_subscribed") {
        toast.info("You're already on the list — we'll be in touch.");
      } else {
        toast.success("You're on the list! Check your inbox for a welcome letter.");
        setName("");
        setEmail("");
      }
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <button
      data-testid={`${idPrefix}-newsletter-submit-btn`}
      type="submit"
      disabled={busy}
      className="inline-flex shrink-0 items-center gap-2 border border-abbey-gold bg-abbey-gold px-7 py-3.5 font-cinzel text-[10px] font-semibold tracking-[0.25em] text-abbey-dark transition-colors duration-300 hover:bg-transparent hover:text-abbey-gold disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : null}
      {busy ? "JOINING..." : "NOTIFY ME"}
    </button>
  );

  if (compact) {
    return (
      <form
        data-testid={`${idPrefix}-newsletter-form`}
        onSubmit={submit}
        className="flex w-full max-w-md items-end gap-4"
      >
        <input
          data-testid={`${idPrefix}-newsletter-email-input`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={inputClass}
          aria-label="Email address"
        />
        {button}
      </form>
    );
  }

  return (
    <form data-testid={`${idPrefix}-newsletter-form`} onSubmit={submit} className="w-full max-w-xl space-y-8">
      <div className="flex items-center gap-3">
        <BellRing className="h-4 w-4 text-abbey-gold" strokeWidth={1.5} />
        <p className="font-cinzel text-[11px] tracking-[0.35em] text-abbey-gold">NEW RELEASE ALERTS</p>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <input
          data-testid={`${idPrefix}-newsletter-name-input`}
          type="text"
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className={inputClass}
          aria-label="Your name (optional)"
        />
        <input
          data-testid={`${idPrefix}-newsletter-email-input`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={inputClass}
          aria-label="Email address"
        />
      </div>
      {button}
    </form>
  );
}
